// apps/widget/src/components/widget-header.tsx
//
// Back, branding, close — the three things a visitor looks for at the top of a
// dialog, in the places they look for them.
//
// Close lives here rather than only on the host's panel because closing has to
// be interceptable: once a scan is under way, discarding it without asking is
// the kind of thing people do not forgive.

import { chooseLocale, getLocale, LOCALE_NAMES, LOCALES, t, type Locale } from '../lib/i18n';
import type { WidgetConfig } from '../lib/api';

interface Props {
  config: WidgetConfig;
  /** Omitted on the first screen, where there is nowhere to go back to. */
  onBack?: (() => void) | undefined;
  /** Absent in the staff view, where the dashboard owns closing. */
  onClose?: (() => void) | undefined;
}

export function WidgetHeader({ config, onBack, onClose }: Props) {
  return (
    <header className="gs-header">
      {onBack ? (
        <button className="gs-header__icon" onClick={onBack} aria-label={t('common.back')}>
          <svg className="gs-flip-rtl" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
              d="M12.5 16 6.5 10l6-6"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ) : (
        // Holds the column so the brand stays optically centred on screen one.
        <span className="gs-header__icon" aria-hidden="true" />
      )}

      <span className="gs-header__brand">
        {config.tenant.logoUrl ? (
          <img src={config.tenant.logoUrl} alt={config.tenant.name} className="gs-header__logo" />
        ) : (
          config.tenant.name
        )}
      </span>

      {/* Language and close share the right, so the brand stays centred. */}
      <span className="gs-header__end">
        <LanguageSwitch available={config.locales} />
        {onClose ? (
          <button className="gs-header__icon" onClick={onClose} aria-label={t('common.close')}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path
                d="m5 5 10 10M15 5 5 15"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        ) : (
          <span className="gs-header__icon" aria-hidden="true" />
        )}
      </span>
    </header>
  );
}

/**
 * The clinic's languages, each in its own words. Only when it offers more than
 * one — a switch with nothing to switch to is noise. The platform's own
 * <select> under a quiet face: on a phone it opens the native picker.
 */
export function LanguageSwitch({ available }: { available: string[] }) {
  const offered = LOCALES.filter((l) => available.includes(l));
  if (offered.length < 2) return null;
  const current = getLocale();

  return (
    <label className="gs-lang">
      <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <circle cx="10" cy="10" r="7.25" stroke="currentColor" strokeWidth="1.5" />
        <path d="M2.75 10h14.5M10 2.75c2 2.1 3 4.5 3 7.25s-1 5.15-3 7.25c-2-2.1-3-4.5-3-7.25s1-5.15 3-7.25Z" stroke="currentColor" strokeWidth="1.5" />
      </svg>
      <span className="gs-lang__code" aria-hidden="true">
        {current.toUpperCase()}
      </span>
      <select
        aria-label={t('common.language')}
        value={current}
        onChange={(e) => void chooseLocale(e.target.value as Locale)}
      >
        {offered.map((code) => (
          <option key={code} value={code}>
            {LOCALE_NAMES[code]}
          </option>
        ))}
      </select>
    </label>
  );
}
